import React, { useContext, useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import { DEVICE_TYPE, getDeviceType, getIsChrome } from '@chownow/cn-web-utils/url';

import { ToastContext } from 'context/toast';

import ModalHeader from 'components/designSystem/Modals/ModalHeader';
import ModalFooter from 'components/designSystem/Modals/ModalFooter';
import ModalFooterButton from 'components/designSystem/Modals/ModalFooter/ModalFooterButton';

import { FormValues, Rating } from './ratingsSurveyModalTypes';
import OrderAgainFollowUpQuestion from './OrderAgainFollowUpQuestion';
import OrderAgainQuestion from './OrderAgainQuestion';
import RatingQuestion from './RatingQuestion';

import { mParticleCustomEvents, mParticleEventTypes } from 'helpers/mParticle';
import { sendEvent } from './eventUtils';

import styles from './RatingsSurveyModal.module.scss';

interface RatingsSurveyModalProps {
  hideModal: () => {};
}

function parseRating(rawRating: number): Rating {
  if (rawRating < 1) {
    return 1;
  }

  if (rawRating > 5) {
    return 5;
  }

  return Number(rawRating) as Rating;
}

function scrollIntoView(element?: HTMLElement): void {
  if (!element) {
    return;
  }
  element.scrollIntoView({ behavior: 'smooth' });
}

interface NavigatorLike {
  userAgent: string;
  vendor: string;
}

export function getIsIosChrome(
  { navigator }: { navigator?: NavigatorLike } = { navigator: window.navigator }
): Boolean {
  return /CriOS/.test(navigator.userAgent) && /Apple/.test(navigator.vendor);
}

export default function RatingsSurveyModal({ hideModal }: RatingsSurveyModalProps) {
  const router = useRouter();

  const queryParams = router.query;
  const restaurantName = (queryParams?.restaurantName || 'your meal') as string;
  const rating = parseRating((queryParams?.rating || 5) as number);

  const formContext = useForm<FormValues>({
    defaultValues: { rating, isSubmitting: false },
  });
  const { toast } = useContext(ToastContext);
  const { handleSubmit, formState, watch } = formContext;
  const { isValid } = formState;

  const isChrome = getIsChrome() || getIsIosChrome();
  const deviceType = getDeviceType();
  const isMobile = window.innerWidth < 768;
  const heightChangeAnimationWaitDuration = isMobile ? 0 : 300;

  const orderAgainResponse = watch('orderAgain');
  const orderAgainFollowUpRef = useRef<HTMLDivElement>(null);
  const orderAgainFollowUpOtherRef = useRef<HTMLDivElement>(null);
  const orderAgainFollowUpElement = orderAgainFollowUpRef.current;

  const followUpResponse = watch('orderAgainFollowUp');
  const otherIsSelected = followUpResponse?.some((choice) => choice === 'Other');

  useEffect(() => {
    // Send mParticle event when survey first loads
    sendEvent(
      queryParams,
      formContext,
      mParticleCustomEvents.viewRatingsSurvey,
      mParticleEventTypes.Navigation
    );

    // Also send mParticle event when the modal is dismissed
    // but hasn't been submitted
    return () => {
      const isSubmitting = formContext.getValues('isSubmitting');

      if (!isSubmitting) {
        sendEvent(
          router.query,
          formContext,
          mParticleCustomEvents.restaurantSurveyModalClose,
          mParticleEventTypes.Navigation
        );
      }
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      scrollIntoView(orderAgainFollowUpElement);
    }, heightChangeAnimationWaitDuration);
  }, [orderAgainResponse, orderAgainFollowUpElement]);

  useEffect(() => {
    setTimeout(() => {
      const orderAgainFollowUpOtherElement = orderAgainFollowUpOtherRef.current;
      scrollIntoView(orderAgainFollowUpOtherElement);
    }, heightChangeAnimationWaitDuration);
  }, [otherIsSelected]);

  function handleFormSubmit(): void {
    // Send event when survey responses are submitted
    sendEvent(
      queryParams,
      formContext,
      mParticleCustomEvents.completeRatingsSurvey,
      mParticleEventTypes.UserPreference
    );

    toast({
      title: 'Thank you!',
      message: 'Your reply helps us make ChowNow better for you',
    });

    // On modal close we need to know whether it's closing
    // because the form was submitted
    formContext.setValue('isSubmitting', true);

    hideModal();
  }

  return (
    <>
      <ModalHeader className={styles.header} title="Rate your meal" isSticky />
      <form
        data-testid="RatingsSurveyModal"
        className={classNames(styles.ratingsSurveyModal, {
          [styles.android]: deviceType === DEVICE_TYPE.ANDROID,
          [styles.chrome]: isChrome,
          [styles.increasedHeight]: Boolean(orderAgainResponse),
        })}
      >
        <div className={styles.questions}>
          <RatingQuestion
            restaurantName={restaurantName}
            rating={rating}
            formContext={formContext}
          />
          <OrderAgainQuestion formContext={formContext} />
          {orderAgainResponse && (
            <OrderAgainFollowUpQuestion
              questionRef={orderAgainFollowUpRef}
              otherButtonRef={orderAgainFollowUpOtherRef}
              orderAgainResponse={orderAgainResponse}
              formContext={formContext}
            />
          )}
        </div>
      </form>
      <ModalFooter className={styles.footer} bottomFixed>
        <ModalFooterButton
          handleClick={handleSubmit(handleFormSubmit)}
          disabled={!isValid}
          type="submit"
        >
          Submit
        </ModalFooterButton>
      </ModalFooter>
    </>
  );
}
